/**
 * The five ways the activity history can be narrowed.
 *
 * Every one of them is the server's: the list is paged, so filtering the page
 * that happens to be loaded would answer a different question from the one
 * being asked -- "the MCP writes among the last twenty-five operations" rather
 * than "the last twenty-five MCP writes".
 */
import { useTranslation } from 'react-i18next'

import type { DatabaseApplication } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import {
  ACTIVITY_CATEGORIES,
  ACTIVITY_SOURCES,
  hasActivityFilters,
  type ActivityFilters,
} from '../activity'

export function ActivityFilterBar({
  tables,
  filters,
  onChange,
}: {
  tables: DatabaseApplication['tables']
  filters: ActivityFilters
  onChange: (filters: ActivityFilters) => void
}) {
  const { t } = useTranslation()

  /** One narrowing changed, with an empty control meaning "not narrowed". */
  function set<K extends keyof ActivityFilters>(
    key: K,
    value: ActivityFilters[K] | undefined,
  ) {
    const next = { ...filters }
    if (value === undefined) delete next[key]
    else next[key] = value
    onChange(next)
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="grid gap-1">
        <Label htmlFor="activity-source" className="text-xs text-muted-foreground">
          {t('activity.source')}
        </Label>
        <NativeSelect
          id="activity-source"
          value={filters.source ?? ''}
          onChange={(event) =>
            set(
              'source',
              event.target.value === '' ? undefined : (event.target.value as never),
            )
          }
        >
          <option value="">{t('activity.anySource')}</option>
          {ACTIVITY_SOURCES.map((source) => (
            <option key={source} value={source}>
              {t(`activity.sources.${source}`)}
            </option>
          ))}
        </NativeSelect>
      </div>

      <div className="grid gap-1">
        <Label htmlFor="activity-table" className="text-xs text-muted-foreground">
          {t('activity.table')}
        </Label>
        <NativeSelect
          id="activity-table"
          value={filters.tableId === undefined ? '' : String(filters.tableId)}
          onChange={(event) =>
            set(
              'tableId',
              event.target.value === '' ? undefined : Number(event.target.value),
            )
          }
        >
          <option value="">{t('activity.anyTable')}</option>
          {tables.map((table) => (
            <option key={table.id} value={table.id}>
              {table.name}
            </option>
          ))}
        </NativeSelect>
      </div>

      <div className="grid gap-1">
        <Label htmlFor="activity-kind" className="text-xs text-muted-foreground">
          {t('activity.kind')}
        </Label>
        <NativeSelect
          id="activity-kind"
          value={filters.category ?? ''}
          onChange={(event) =>
            set(
              'category',
              event.target.value === '' ? undefined : (event.target.value as never),
            )
          }
        >
          <option value="">{t('activity.anyKind')}</option>
          {ACTIVITY_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {t(`activity.kinds.${category}`)}
            </option>
          ))}
        </NativeSelect>
      </div>

      <div className="grid gap-1">
        <Label htmlFor="activity-from" className="text-xs text-muted-foreground">
          {t('activity.from')}
        </Label>
        <Input
          id="activity-from"
          type="date"
          className="w-40"
          value={filters.from ?? ''}
          onChange={(event) =>
            set('from', event.target.value === '' ? undefined : event.target.value)
          }
        />
      </div>

      <div className="grid gap-1">
        <Label htmlFor="activity-to" className="text-xs text-muted-foreground">
          {t('activity.to')}
        </Label>
        <Input
          id="activity-to"
          type="date"
          className="w-40"
          value={filters.to ?? ''}
          onChange={(event) =>
            set('to', event.target.value === '' ? undefined : event.target.value)
          }
        />
      </div>

      {hasActivityFilters(filters) && (
        <Button variant="ghost" size="sm" onClick={() => onChange({})}>
          {t('activity.clearFilters')}
        </Button>
      )}
    </div>
  )
}
